import { toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type {
  CarePlan,
  QuestionnaireResponse,
  RequestGroup,
  ServiceRequest,
} from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface AddTestToOrderParams {
  testCode: string;
  compendiumId: string;
  requestGroupId: string;
}

interface AddTestToOrderResponse {
  requestGroup: RequestGroup;
  serviceRequest: ServiceRequest;
  carePlan: CarePlan;
  questionnaireResponse?: QuestionnaireResponse;
}

export const useAddTestToOrder = ({
  patientId,
  encounterId,
  requestGroupId,
}: {
  patientId: string;
  encounterId: string;
  requestGroupId: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<AddTestToOrderResponse, AddTestToOrderParams>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Labs.ADD_TEST_TO_ORDER({
        patientId,
        requestGroupId,
      }),
      mutationFn: (params) => {
        return axiosInstance.post(`/labs/order/${requestGroupId}/add-test`, {
          ...params,
        });
      },
      onError: () => {
        toast.error("Failed to add test to order");
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.DEFAULT_V2({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
            patientId,
            encounterId,
          }),
        });
      },
    },
  });
};
